import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Pressable, Skeleton, StatStrip as Strip, cn } from '@orgus/ui';

export interface Stat {
  /** Names the measure and the period: "Spent in October". */
  label: string;
  value: ReactNode;
  /** After the value, quiet: "of 100", "sessions". */
  unit?: ReactNode;
  /** One line under the value. */
  sub?: ReactNode;
  /** Colour only for what a person may act on. */
  tone?: 'attention' | 'danger';
  /** Opens the view that lists exactly what the number counts. */
  to?: string;
  onOpen?: () => void;
}

/** A cell of the shared stat strip (StatTile's frame): the same padding everywhere. */
const CELL = 'flex min-w-0 flex-col gap-1 bg-surface px-4 py-3 sm:px-8 sm:py-4';

const TONE = { attention: 'text-status-attention', danger: 'text-status-danger' } as const;

function Cell({ s }: { s: Stat }) {
  const navigate = useNavigate();
  const body = (
    <>
      <span className="truncate text-caption text-fg-subtle">{s.label}</span>
      <span className={cn('flex min-w-0 items-baseline gap-1 font-display text-metric num-display', s.tone ? TONE[s.tone] : 'text-fg')}>
        <span className="truncate">{s.value}</span>
        {s.unit != null && <span className="shrink-0 font-sans text-ui font-normal text-fg-subtle">{s.unit}</span>}
      </span>
      {s.sub != null && <span className="line-clamp-1 text-caption text-fg-subtle">{s.sub}</span>}
    </>
  );
  const open = s.onOpen ?? (s.to ? () => navigate(s.to!) : null);
  return open ? (
    <Pressable onClick={open} data-metric={s.label} className={cn(CELL, 'text-left transition-colors duration-(--duration-fast) hover:bg-hover')}>
      {body}
    </Pressable>
  ) : (
    <div data-metric={s.label} className={CELL}>
      {body}
    </div>
  );
}

const COLS: Record<number, string> = { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5', 6: 'lg:grid-cols-3 2xl:grid-cols-6' };

/**
 * The numbers at the top of the fleet and money pages, as the shared StatStrip draws them on
 * every page: one panel, cells set apart by thin inset lines; grey unless a number needs a person.
 */
export function StatStrip({ stats, label, className }: { stats: Stat[]; label: string; className?: string }) {
  return (
    <Strip label={label} className={cn(COLS[stats.length], className)}>
      {stats.map((s) => (
        <Cell key={s.label} s={s} />
      ))}
    </Strip>
  );
}

export function StatStripSkeleton({ cells = 4, className }: { cells?: number; className?: string }) {
  return (
    <div className={cn('inset-dividers grid grid-cols-2 overflow-hidden rounded-card border border-border bg-surface shadow-elevation-flat', COLS[cells], className)} aria-hidden>
      {Array.from({ length: cells }, (_, i) => (
        <div key={i} className={cn(CELL, 'gap-2')}>
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}
